// src/features/locations/AddLocationModal.jsx
//
// Modal for creating a single new location via POST /api/locations.
// Replaces the placeholder toast on AddLocationButton (closes audit §3.5).
//
// Architecture (per slice M2 brief + audited Phase-1 invariants):
//   - Inline T + S styles only. No new tokens, no new deps, no modal lib.
//   - Reuses existing locationsClient.addLocation (NOT a new createLocation
//     method — addLocation already POSTs, validates id, busts cache, audits).
//   - parseNumericField defined locally — no shared util exists yet (Lesson 6
//     created the rule but the helper was never extracted). Local now;
//     promote to a shared util when a 2nd caller appears.
//   - CURRENCIES list inline — no shared constant exists. 12 ISO 4217 codes
//     covering current + likely-near-term markets. Promote when a 2nd caller
//     appears or when the list needs to drive the bulk-import currencies table.
//   - A11y: Escape closes, focus returns to the trigger button on close,
//     focus moves to the first input on open.
//   - Admin gating lives on the SERVER (POST returns 403 for non-admin).
//     The trigger button is currently NOT admin-gated in PortfolioView; that
//     is a separate slice. The modal handles a 403 defensively in Gate 2.
//
// Gate 1 = scaffold only: render fields, run client-side validation on
// submit, surface errors, cancel closes. NO POST wiring yet — the submit
// handler stops at validation and logs a marker. Gate 2 wires createLocation.

import React, { useEffect, useRef, useState } from "react";
import { T } from "../../app/tokens.js";
import { STAGES, REGIONS } from "../../app/tokens.js";
import { Modal } from "../../components/Modal.jsx";
// E3 chrome polish — was: `import { CURRENCIES } from "../../app/currencies.js";`.
// L6.5 walk-through dropped the visible Currency picker from the modal in
// favor of a Country→Currency lookup with a USD fallback (see
// inferCurrencyFromCountry below). The CURRENCIES list is still exported
// from app/currencies.js for the LocationDetail edit screen and lease forms.
import { log } from "../../utils/logger.js";
import { parseNumericField } from "../../utils/parseNumericField.js";
import { addLocation } from "../../data/locationsClient.js";
import { getActiveMarkets } from "../../data/marketsClient.js";
import { getNextLocationIds } from "../../data/adminClient.js";
import {
  AuthError,
  ConflictError,
  NetworkError,
  ValidationError,
} from "../../data/errors.js";

// ─── Constants ────────────────────────────────────────────────────────────

// Mirrors locationsClient ID_REGEX; duplicated here so the modal can
// surface a friendly message before round-tripping to the client method.
const ID_REGEX = /^(AMER|EMEA|APAC)-[A-Z]{3}-\d{2}$/;

// parseNumericField was extracted to a shared module during Slice L5 Gate 1
// (Option C — promote helpers, keep form bodies separate). AddLocationModal
// continues to consume it via the import above.

const STAGE_KEYS = Object.keys(STAGES); // canonical lifecycle order, including Draft

// E3 chrome polish — was: ["id","market","country","currency","region","stage"].
// L6.5 walk-through trimmed the modal to legacy's 4 required fields. Currency
// is no longer user-entered (always inferred — see inferCurrencyFromCountry).
// ADDR1: address is now required at all layers.
// CTRY1: country restored to required — the edit panel already validates it
// via buildDiffPayload.REQUIRED_FIELDS; the modal must match.
const REQUIRED_FIELDS = ["id", "market", "region", "stage", "address", "country"];

// E3 chrome polish — was: ["sqft","installedCapacity","maxCapacity","floors","lat","lng"].
// L6.5 walk-through dropped capacity / lat / lng / floors from the modal
// per "modal is a quick start, not every-question" — those move to the
// (future) Edit Location detail view. Sqft survives because legacy shows it.
const NUMERIC_FIELDS = ["sqft"];

// E3 chrome polish — was: ["locationAlias","businessUnit","district","address","suite","health"].
// L6.5 walk-through dropped M&A, District, Health from the modal.
// Suite stays — the legacy "Suite / Floors" combined UI input writes its
// raw value into `suite` (the backend `floors` field is left untouched and
// can be filled later via the Edit screen — see Q4 thread in chat).
// ADDR1: address promoted to REQUIRED_FIELDS; only locationAlias + suite remain optional.
const OPTIONAL_STRING_FIELDS = ["locationAlias", "suite"];

// E3 chrome polish — new: Country → Currency lookup. Backs the silent
// currency-inference path now that the picker is gone. Case-insensitive
// match on country name (or common alias). Anything not in the table —
// including a blank country — falls back to USD. Promote to a shared
// util when a 2nd caller appears (M2 brief pattern).
//
// E3 chrome polish — pruned (architect review): only the 9 currency codes
// seeded in the `currencies` reference table are valid here. Mapping a
// country to a code that's not seeded would FK-fail (23503) on insert and
// surface as a 500 to the user.
//
// LSE-FX-MISSING-RATES (2026-05-22): BRL/INR/ISK now have FX rates seeded
// (migration 0040). Brazil, India, and Iceland have been moved from the
// old COUNTRIES_NEEDING_CURRENCY_SEED fallback into this map so they
// correctly infer their native currency on location creation.
const COUNTRY_TO_CURRENCY = {
  "usa": "USD", "united states": "USD", "united states of america": "USD", "us": "USD",
  "canada": "CAD",
  "uk": "GBP", "united kingdom": "GBP", "great britain": "GBP", "england": "GBP",
  "switzerland": "CHF",
  "uae": "AED", "united arab emirates": "AED",
  "australia": "AUD",
  "singapore": "SGD",
  "japan": "JPY",
  "bvi": "USD", "british virgin islands": "USD",
  // EUR trio — extend as new EUR markets come online.
  "germany": "EUR", "france": "EUR", "ireland": "EUR",
  // LSE-FX-MISSING-RATES: seeded 2026-05-22
  "brazil": "BRL",
  "india": "INR",
  "iceland": "ISK",
};
function inferCurrencyFromCountry(country) {
  if (!country || typeof country !== "string") return "USD";
  const key = country.trim().toLowerCase();
  return COUNTRY_TO_CURRENCY[key] ?? "USD";
}

// ─── Inline style block (S — local to this component) ────────────────────

const S = {
  body: { padding: "16px 20px", overflowY: "auto", flex: 1 },
  grid: {
    display: "grid", gridTemplateColumns: "1fr 1fr",
    gap: "12px 16px",
  },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  // E3 chrome polish — was: 12px / weight 600 / dark / Title-Case as-typed.
  // Restored legacy treatment: 11px UPPERCASE w/ letter-spacing in mid gray.
  // textTransform handles the casing — JSX label strings stay as-typed
  // so test assertions like getByLabelText("Country") still match.
  label: {
    fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.mid,
    textTransform: "uppercase", letterSpacing: 0.4,
  },
  required: { color: T.red, marginLeft: 2 },
  input: {
    padding: "7px 10px", fontSize: T.fsBase, color: T.dark,
    background: T.white,
    borderWidth: 1, borderStyle: "solid", borderColor: T.lightGray,
    borderRadius: 6, outline: "none",
  },
  inputError: { borderColor: T.red },
  fieldError: { fontSize: T.fsXs, color: T.red, marginTop: 2 },
  // E3 chrome polish — was: `moreToggle` style for the "+ More fields"
  // disclosure button. L6.5 walk-through dropped the disclosure entirely;
  // every modal field is now flat. Style block removed with the button.
  // E3 chrome polish — new: `fullRow` spans both grid columns for fields
  // that legacy renders as full-width rows (ID, Address, Suite/Floors).
  fullRow: { gridColumn: "1 / -1" },
  footer: {
    padding: "12px 20px",
    borderTop: `1px solid ${T.lightGray}`,
    display: "flex", justifyContent: "flex-end", gap: 8,
  },
  btnCancel: {
    padding: "7px 14px", fontSize: T.fsSm5, fontWeight: T.fwSemibold,
    color: T.dark, background: T.white,
    border: `1px solid ${T.lightGray}`, borderRadius: 6, cursor: "pointer",
  },
  btnSubmit: {
    padding: "7px 14px", fontSize: T.fsSm5, fontWeight: T.fwBold,
    color: T.white, background: T.blue,
    border: "none", borderRadius: 6, cursor: "pointer",
  },
};

// Empty form state — every key the modal ever touched is present so React
// inputs stay controlled even after the L6.5 trim. The dropped fields
// (currency / mAndA / district / floors / installedCapacity /
// maxCapacity / health / lat / lng) just aren't rendered anymore; keeping
// them in EMPTY_FORM means the applyErrorToUI hasOwnProperty check still
// recognizes any legacy server-side field hint and routes it sensibly.
//
// E3 chrome polish — was: stage: "". Legacy pre-selects "Operational"
// (the typical create-time pick). User confirmed in Q2.
const EMPTY_FORM = {
  id: "", market: "", country: "", currency: "", region: "", stage: "operational",
  locationAlias: "", mAndA: "", district: "",
  address: "", suite: "", floors: "",
  sqft: "", installedCapacity: "", maxCapacity: "",
  health: "", lat: "", lng: "",
};

// ─── Component ────────────────────────────────────────────────────────────

export function AddLocationModal({ isOpen, onClose, onCreated, onOpenMarketSetup }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});      // per-field error strings
  const [formError, setFormError] = useState(""); // form-level error (403/5xx/network)
  // E3 chrome polish — was: `const [showMore, setShowMore] = useState(false);`.
  // The "+ More fields" disclosure was removed in L6.5; every field is flat.
  const [submitting, setSubmitting] = useState(false);

  // ADMIN-LOCATION-IDS-SIMPLIFY-S1: next-ID suggestions, one per market,
  // fetched from the same endpoint the admin Location IDs tab uses. Used to
  // auto-fill the Location ID field once the typed market matches a known
  // market name. autoFilledId tracks the value WE wrote so a later manual
  // edit by the user is never silently clobbered.
  const [nextIdSuggestions, setNextIdSuggestions] = useState([]);
  const autoFilledIdRef = useRef(null);
  const [markets, setMarkets] = useState([]);
  const [marketsLoading, setMarketsLoading] = useState(false);
  const [marketsError, setMarketsError] = useState("");
  const [marketMode, setMarketMode] = useState("existing");
  const [createdMarket, setCreatedMarket] = useState(null);

  // Focus the first input when the modal opens. Focus-return to the trigger
  // is handled by Modal.jsx (§12 canonical pattern).
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const id = requestAnimationFrame(() => firstInputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setMarketsLoading(true);
    setMarketsError("");
    getActiveMarkets()
      .then((rows) => {
        if (!cancelled) setMarkets(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setMarkets([]);
          setMarketsError(err?.message || "Could not load markets.");
        }
      })
      .finally(() => {
        if (!cancelled) setMarketsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getNextLocationIds()
      .then((rows) => { if (!cancelled) setNextIdSuggestions(Array.isArray(rows) ? rows : []); })
      .catch(() => { /* non-critical: auto-fill is a convenience, not required */ });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Reset form whenever the modal transitions to closed, so re-opens are clean.
  useEffect(() => {
    if (!isOpen) {
      setForm(EMPTY_FORM);
      setErrors({});
      setFormError("");
      // E3 chrome polish — was: setShowMore(false). Disclosure removed.
      setSubmitting(false);
      setNextIdSuggestions([]);
      autoFilledIdRef.current = null;
      setMarkets([]);
      setMarketsError("");
      setMarketsLoading(false);
      setMarketMode("existing");
      setCreatedMarket(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // ── Validation ──────────────────────────────────────────────────────────

  function validate(values) {
    const errs = {};
    for (const field of REQUIRED_FIELDS) {
      if (!values[field] || String(values[field]).trim() === "") {
        errs[field] = "Required.";
      }
    }
    if (values.id && !errs.id && !ID_REGEX.test(values.id)) {
      errs.id = "Must match REGION-CITYCODE-SEQ (e.g. AMER-SFO-01).";
    }
    if (marketMode === "existing" && !values.marketId) {
      errs.market = "Select an existing market or choose Create new market.";
    }
    if (marketMode === "new" && String(values.market || "").trim() === "") {
      errs.market = "Enter a market name.";
    }
    return errs;
  }

  // ── Handlers ────────────────────────────────────────────────────────────

  function handleChange(field, value) {
    // If the user is directly editing the id field and the new value no
    // longer matches what we auto-filled, stop treating it as "ours" so a
    // later market change never overwrites their manual edit.
    if (field === "id" && value !== autoFilledIdRef.current) {
      autoFilledIdRef.current = null;
    }

    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
    if (formError) setFormError("");
  }

  function handleMarketSelection(value) {
    if (value === "__new__") {
      setMarketMode("new");
      setForm((f) => ({ ...f, market: "", marketId: "" }));
      autoFilledIdRef.current = null;
      if (errors.market) setErrors((e) => ({ ...e, market: undefined }));
      return;
    }
    const selected = markets.find((market) => market.id === value);
    if (!selected) {
      // A selection can arrive in the same tick that the active-market
      // request resolves. Keep the entity ID as the source of truth and let
      // the next render replace the temporary label with the canonical name.
      // The server still validates the ID against the active registry.
      const pendingSuggestion = nextIdSuggestions.find((s) => s.marketId === value);
      setMarketMode("existing");
      setForm((f) => ({
        ...f,
        market: pendingSuggestion?.marketName || value,
        marketId: value,
        ...(pendingSuggestion?.region ? { region: pendingSuggestion.region } : {}),
        ...(pendingSuggestion?.nextId && (f.id === "" || f.id === autoFilledIdRef.current)
          ? { id: pendingSuggestion.nextId }
          : {}),
      }));
      if (pendingSuggestion?.nextId) autoFilledIdRef.current = pendingSuggestion.nextId;
      if (errors.market) setErrors((e) => ({ ...e, market: undefined }));
      setMarketMode("existing");
      return;
    }
    const suggestion = nextIdSuggestions.find((s) => s.marketId === selected.id);
    const currentIdIsBlankOrOurs = form.id === "" || form.id === autoFilledIdRef.current;
    if (suggestion && currentIdIsBlankOrOurs) {
      autoFilledIdRef.current = suggestion.nextId;
    }
    setMarketMode("existing");
    setForm((f) => ({
      ...f,
      market: selected.name,
      marketId: selected.id,
      region: selected.region || f.region,
      ...(suggestion && currentIdIsBlankOrOurs ? { id: suggestion.nextId } : {}),
    }));
    if (errors.market) setErrors((e) => ({ ...e, market: undefined }));
    if (formError) setFormError("");
  }

  function handleCancel() {
    onClose?.();
  }

  // Build the POST payload from the form state.
  //   - Required fields always present (validator already enforced non-empty).
  //   - Optional strings included only when non-empty.
  //   - Numerics routed through parseNumericField; null → omit entirely.
  // ADDR1: all string fields are trimmed before sending so client and server agree.
  function buildPayload(values) {
    const payload = {};
    for (const f of REQUIRED_FIELDS) {
      payload[f] = String(values[f]).trim();
    }
    // CTRY1: country is now required (handled by the REQUIRED_FIELDS loop above).
    // Currency is still inferred from country — blank country falls back to USD.
    payload.currency = inferCurrencyFromCountry(payload.country);
    for (const f of OPTIONAL_STRING_FIELDS) {
      const v = values[f];
      if (v != null && String(v).trim() !== "") payload[f] = String(v).trim();
    }
    for (const f of NUMERIC_FIELDS) {
      const n = parseNumericField(values[f]);
      if (n !== null) payload[f] = n;
    }
    delete payload.market;
    if (marketMode === "existing") {
      payload.marketId = values.marketId;
    } else {
      payload.newMarket = {
        name: String(values.market).trim(),
        region: payload.region,
      };
    }
    return payload;
  }

  // Map a typed error from addLocation() into either a per-field error
  // (id / market / etc.) or a form-level banner. Centralized so the
  // four error cases (400/409/403/5xx-or-network) stay symmetric.
  function applyErrorToUI(err) {
    // 409: duplicate id from PK collision — surface inline under the id input.
    if (err instanceof ConflictError) {
      if (err.details?.field === "newMarket.name") {
        setErrors({ market: err.message });
        return;
      }
      setErrors({ id: "A location with this ID already exists." });
      return;
    }
    // 400: server validation. Honor the {field, message} hint when present;
    // otherwise show the message at the form level so the user still sees it.
    if (err instanceof ValidationError) {
      const field = err.details?.field;
      const msg = err.message || "Invalid request.";
      if (field && Object.prototype.hasOwnProperty.call(EMPTY_FORM, field)) {
        setErrors({ [field]: msg });
      } else {
        setFormError(msg);
      }
      return;
    }
    // 403: should never happen — admin button → admin modal → admin POST.
    // If it does, it means an admin button rendered to a non-admin (a real
    // bug worth investigating), so log.error and surface a clean message.
    if (err instanceof AuthError) {
      log.error("[AddLocationModal] 403 from POST /api/locations - non-admin reached the modal", {
        name: err.name, status: err.status, message: err.message,
      });
      setFormError("You don't have permission to create locations.");
      return;
    }
    // 5xx / network / anything else — generic message, full context to log.
    log.error("[AddLocationModal] create failed", {
      name: err?.name, message: err?.message,
      status: err?.status, isNetwork: err instanceof NetworkError,
    });
    setFormError("Something went wrong. Please try again.");
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (submitting) return; // double-submit guard

    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setErrors({});
    setFormError("");
    setSubmitting(true);

    const payload = buildPayload(form);
    try {
      const created = await addLocation(payload);
      // New-market creates pause long enough to offer the existing market-photo
      // setup surface. Existing-market creates retain the original close-first
      // behavior.
      if (marketMode === "new") {
        setCreatedMarket(created);
        onCreated?.(created);
        return;
      }
      // Success: close FIRST (synchronous state flip in the parent), then
      // dispatch the onCreated callback which is allowed to be async work
      // (PortfolioView's handleCreated bumps a version → triggers a refetch).
      // Closing first avoids any window where the modal sits over a
      // loading state during the refetch (Gate 3 brief risk flag).
      onClose?.();
      onCreated?.(created);
    } catch (err) {
      applyErrorToUI(err);
    } finally {
      // Always clear submitting so the user can retry after an error.
      setSubmitting(false);
    }
  }

  // ── Render helpers ──────────────────────────────────────────────────────

  function textField(name, label, opts = {}) {
    const required = REQUIRED_FIELDS.includes(name);
    const err = errors[name];
    return (
      <div style={S.field}>
        <label htmlFor={`add-loc-${name}`} style={S.label}>
          {label}{required && <span style={S.required}>*</span>}
        </label>
        <input
          id={`add-loc-${name}`}
          ref={opts.firstRef ? firstInputRef : undefined}
          type={opts.type || "text"}
          value={form[name]}
          onChange={(e) => handleChange(name, e.target.value)}
          placeholder={opts.placeholder || ""}
          data-testid={opts.testId || `add-location-field-${name}`}
          style={{ ...S.input, ...(err ? S.inputError : null) }}
          aria-invalid={!!err}
          aria-describedby={err ? `add-loc-${name}-err` : undefined}
        />
        {err && (
          <span id={`add-loc-${name}-err`} style={S.fieldError}>{err}</span>
        )}
      </div>
    );
  }

  function selectField(name, label, options) {
    const required = REQUIRED_FIELDS.includes(name);
    const err = errors[name];
    return (
      <div style={S.field}>
        <label htmlFor={`add-loc-${name}`} style={S.label}>
          {label}{required && <span style={S.required}>*</span>}
        </label>
        <select
          id={`add-loc-${name}`}
          value={form[name]}
          onChange={(e) => handleChange(name, e.target.value)}
          data-testid={`add-location-field-${name}`}
          style={{ ...S.input, ...(err ? S.inputError : null) }}
          aria-invalid={!!err}
        >
          <option value="">- Select -</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {err && (
          <span style={S.fieldError}>{err}</span>
        )}
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      ariaLabelledBy="add-location-modal-title"
      testId="add-location-modal"
      overlayTestId="add-location-modal-overlay"
      title="Add new office"
      subTitle="Fill in what you know - everything can be edited later"
      closeTestId="add-location-modal-close-x"
    >
        <form onSubmit={handleSubmit}>
          <div style={S.body}>
            {formError && (
              <div
                role="alert"
                data-testid="add-location-modal-form-error"
                style={{
                  background: T.utilRed,
                  borderWidth: 1, borderStyle: "solid", borderColor: T.red,
                  color: T.red, padding: "8px 12px", borderRadius: 6,
                  fontSize: T.fsSm5, marginBottom: 12,
                }}
              >{formError}</div>
            )}
            {createdMarket && (
              <div
                role="status"
                data-testid="add-location-market-created"
                style={{
                  background: T.utilGreen, border: `1px solid ${T.green}`,
                  color: T.dark, padding: "10px 12px", borderRadius: 6,
                  fontSize: T.fsSm5, marginBottom: 12,
                }}
              >
                <strong>{createdMarket.market || form.market}</strong> was created and linked to this office.
                <button
                  type="button"
                  data-testid="add-location-open-market-setup"
                  onClick={() => {
                    onClose?.();
                    onOpenMarketSetup?.(createdMarket);
                  }}
                  style={{
                    display: "block", marginTop: 8, padding: "6px 10px",
                    border: `1px solid ${T.green}`, borderRadius: 5,
                    background: T.white, color: T.blue, cursor: "pointer",
                    fontWeight: T.fwSemibold,
                  }}
                >
                  Open market setup
                </button>
              </div>
            )}
            {/* E3 chrome polish — was: a single 6-field required grid + a
                "+ More fields" disclosure exposing 12 optional fields.
                L6.5 walk-through flattened to legacy's exact field set:
                ID (full row), Market | Alias, Country | Region, Stage |
                Sqft, Address (full row), Suite/Floors (full row).
                Currency / Health / M&A / District / Capacity /
                Lat / Lng all dropped — Edit-after handles the long tail. */}
            <div style={S.grid}>
              <div style={S.fullRow}>
                {textField("id", "Office ID", {
                  placeholder: "e.g. AMER-NYC-02", firstRef: true,
                })}
              </div>
              <div style={S.field}>
                <label htmlFor="add-loc-market" style={S.label}>
                  Market<span style={S.required}>*</span>
                </label>
                <select
                  id="add-loc-market"
                  value={marketMode === "new" ? "__new__" : (form.marketId || "")}
                  onChange={(e) => handleMarketSelection(e.target.value)}
                  data-testid="add-location-field-market"
                  disabled={marketsLoading || !!marketsError || !!createdMarket}
                  style={{ ...S.input, ...(errors.market ? S.inputError : null) }}
                  aria-invalid={!!errors.market}
                  aria-describedby={errors.market ? "add-loc-market-err" : undefined}
                >
                  <option value="">
                    {marketsLoading ? "Loading markets…" : "Select a market"}
                  </option>
                  {markets.map((market) => (
                    <option key={market.id} value={market.id}>
                      {market.name} ({market.region})
                    </option>
                  ))}
                  <option value="__new__">+ Create new market</option>
                </select>
                {marketsError && (
                  <span style={S.fieldError}>
                    {marketsError} Refresh and try again.
                  </span>
                )}
                {errors.market && (
                  <span id="add-loc-market-err" style={S.fieldError}>{errors.market}</span>
                )}
              </div>
              {marketMode === "new" && textField("market", "New market name", {
                placeholder: "e.g. Tokyo",
                testId: "add-location-field-new-market-name",
              })}
              {textField("locationAlias", "Office Alias", {
                placeholder: "e.g. FiDi, Flatiron",
              })}
              {textField("country", "Country", { placeholder: "e.g. Japan" })}
              {selectField("region", "Region",
                REGIONS.map((r) => ({ value: r, label: r }))
              )}
              {selectField("stage", "Stage",
                STAGE_KEYS.map((k) => ({ value: k, label: STAGES[k].label }))
              )}
              {textField("sqft", "Sq Ft", { placeholder: "e.g. 45000" })}
              <div style={S.fullRow}>
                {textField("address", "Address", {
                  placeholder: "e.g. 1-1 Shibuya, Tokyo 150-0002, Japan",
                })}
              </div>
              <div style={S.fullRow}>
                {/* E3 chrome polish — was: separate Suite + Floors inputs in
                    the more-fields panel. Legacy collapses them to one input.
                    Raw value is stored in form.suite; backend `floors`
                    stays untouched and can be filled via Edit later. */}
                {textField("suite", "Suite / Floors", {
                  placeholder: "e.g. Suite 125-F or Floor 2 & Rooftop",
                })}
              </div>
            </div>
          </div>

          <div style={S.footer}>
            <button
              type="button"
              className="wos-btn"
              onClick={handleCancel}
              style={S.btnCancel}
              data-testid="add-location-modal-cancel"
            >Cancel</button>
            <button
              type="submit"
              className="wos-btn"
              disabled={submitting}
              style={S.btnSubmit}
              data-testid="add-location-modal-submit"
            >
              {/* E3 chrome polish — was: "Create Location". Restored legacy
                  "+ Add office" framing. Busy state stays "Creating…" to
                  avoid breaking the in-flight unit-test assertion. */}
              {submitting ? "Creating…" : "+ Add office"}
            </button>
          </div>
        </form>
    </Modal>
  );
}

// Exported for unit tests — keeps the local helpers verifiable without
// reaching into the component instance.
// E3 chrome polish — was: also exported `CURRENCIES`. Picker dropped in
// L6.5; replaced by `inferCurrencyFromCountry` for direct unit-test access.
export const __testables = {
  parseNumericField, ID_REGEX, inferCurrencyFromCountry,
  REQUIRED_FIELDS, NUMERIC_FIELDS, OPTIONAL_STRING_FIELDS,
};
